import { G } from '@mobily/ts-belt';
import { match } from 'ts-pattern';
import { z } from 'zod';

import { ZodFileUploadInput } from '@Common/types/bo/file_upload';
import { LegalEntityValues } from '@Common/types/bo/users/users';
import { buildZodBoListQueryParsed } from '@Common/types/bo/utils/build_zod_bo_list_query';
import {
  zodDecimalAmount,
  zodDecimalAmount4,
  zodDecimalAmount5,
  zodIntegerAmount2,
} from '@Common/types/currency/currency';
import {
  ZodB2bInstrumentAssetType,
  ZodB2bInstrumentType,
  ZodInstrumentAssetType,
  ZodInstrumentManagementType,
  ZodInstrumentMutualFundAssetClass,
  ZodInstrumentType,
  ZodSharesStatus,
} from '@Common/types/instruments/instrument';
import { ZodIDPathParam, ZodId } from '@Common/types/request';
import { ZodSuccessResponse } from '@Common/types/response';
import { createZodArrayable } from '@Common/types/zod/zod-arrayable';
import { ZodBrandedDateString } from '@Common/types/zod/zod-branded-date';
import { ZodDateString } from '@Common/types/zod/zod-date-string';
import { endpoint, type GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodIntegerLike } from '@Common/types/zod/zod-number-like';
import { ZodNumberQuery } from '@Common/types/zod/zod-number-query';
import { nullableZodObjectProperties } from '@Common/types/zod/zod-object';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

import type { BoListQueryBase } from '@Common/types/bo/bo_list_query';

export const ZodCountry = ZodTrimmedString.length(2);

export const ZodInstrumentBrokerStatus = z.enum(['active', 'inactive']);
export type InstrumentBrokerStatus = z.TypeOf<typeof ZodInstrumentBrokerStatus>;

export const FundinfoPricingFrequencyValues = [
  'daily',
  'weekly',
  'biweekly',
  'monthly',
  'quarterly',
  'semi_annual',
  'annual',
  'semi_monthly',
  'bi_monthly',
  'ongoing',
] as const;
export const zodFundinfoPricingFrequency = z.enum(FundinfoPricingFrequencyValues);
export type FundinfoPricingFrequency = z.infer<typeof zodFundinfoPricingFrequency>;

export const InstrumentMutualFundLegalStatusValues = [
  'FCPE',
  'FCP',
  'SICAV',
  'SPPICAV',
  'MONETAIRE',
  'SICAF',
  'FCIMT',
  'PIF',
  'FCPI',
  'CIPF',
  'structured_products',
  'euro_fund_life_insurance_scheme',
  'FCPR',
  'unit_trust',
  'OEIC',
  'ICVC',
  'OPCI',
  'scpi_real_estate',
  'ICAV',
  'FPVG',
  'FIAR',
  'FPCI',
  'open_ended_umbrella_unit_trust',
  'cayman_ltd',
  'LLC',
  'SCI',
  'FPS',
  'FIC',
  'OFS',
  'FFS',
  'SCA',
  'FCC',
  'SCP',
  'ELTIF',
  'internal_fund',
  'SC',
  'ETC',
  'FIP',
  'EMTN',
  'AD',
  'ACTION',
  'DIVERS',
] as const;

export const ZodInstrumentMutualFundLegalStatus = z.enum(InstrumentMutualFundLegalStatusValues);
export type InstrumentMutualFundLegalStatus = z.TypeOf<typeof ZodInstrumentMutualFundLegalStatus>;

export const InstrumentStructuredProductTypeValues = [
  'autocall',
  'phoenix',
  'reverse_convertible',
  'capital_guaranteed',
  'cln',
  'amc',
  'other',
] as const;
export const ZodStructuredProductType = z.enum(InstrumentStructuredProductTypeValues);
export type StructuredProductType = z.TypeOf<typeof ZodStructuredProductType>;

export const AssetPlacementTypeValues = ['public', 'private'] as const;
export const ZodAssetPlacementType = z.enum(AssetPlacementTypeValues);
export type AssetPlacementType = z.TypeOf<typeof ZodAssetPlacementType>;

export const CustomExecutionFlow = ['qs99', 'manual'] as const;
export const ZodCustomExecutionFlow = z.enum(CustomExecutionFlow);

export const ZodGetInstrumentsResponseItem = z.object({
  id: ZodId,
  symbol: ZodTrimmedString,
  name: ZodTrimmedString.nullable(),
  exchange: ZodTrimmedString.nullable(),
  country_code: ZodCountry.nullable(),
  class: ZodTrimmedString.nullable(),
  status: ZodInstrumentBrokerStatus,
  tradable: z.boolean(),
  fractionable: z.boolean().nullable(),
  created_at: z.date(),
  logo_path: ZodTrimmedString.nullable(),
  short_name: ZodTrimmedString.nullable(),
  short_description: ZodTrimmedString.nullable(),
  isin: ZodTrimmedString.nullable(),
  is_enabled: z.boolean(),
  is_halted: z.boolean(),
  country_codes_tradable: z.array(ZodCountry),
  country_codes_sell_only: z.array(ZodCountry),
  country_codes_suspended: z.array(ZodCountry),
  type: ZodInstrumentType.or(ZodB2bInstrumentType),
  asset_type: ZodInstrumentAssetType.or(ZodB2bInstrumentAssetType),
  five_year_beta: z.number().nullable(),
  five_year_beta_fallback: z.number().nullable(),
  is_pea_eligible: z.boolean(),
  legal_status: ZodInstrumentMutualFundLegalStatus.nullable(),
  // also served by the public API, keeps the legacy schema until https://sh4res.atlassian.net/browse/LIMA-995
  last_nav_date: ZodDateString.nullable(),
  last_nav: z.number().nullable(),
  pricing_frequency: zodFundinfoPricingFrequency.nullable(),
  management_company: ZodTrimmedString.nullable(),
  risk: z.number().nullable(),
  nav_cut_off_buy: ZodTrimmedString.nullable(),
  nav_cut_off_sell: ZodTrimmedString.nullable(),
  admin_cut_off_buy: ZodTrimmedString.nullable(),
  admin_cut_off_sell: ZodTrimmedString.nullable(),
  announcement_delay_buy: z.number().int().nullable(),
  announcement_delay_sell: z.number().int().nullable(),
  custom_execution_flow: ZodCustomExecutionFlow.nullable(),
  // also served by the public API, keeps the legacy schema until https://sh4res.atlassian.net/browse/LIMA-995
  maturity_date: ZodDateString.nullable(),
  performance: z.number().nullable(),
  support_code: ZodTrimmedString.nullable(),
  is_floor_price_underlying: z.boolean(),
});
export type GetInstrumentsResponseItem = z.TypeOf<typeof ZodGetInstrumentsResponseItem>;
const ZodGetInstrumentsResponse = z.array(ZodGetInstrumentsResponseItem);
export type GetInstrumentsResponse = z.TypeOf<typeof ZodGetInstrumentsResponse>;
export const InstrumentDocumentLanguageValues = [
  'FR',
  'EN',
  'PL',
  'PT',
  'SV',
  'LV',
  'HU',
  'SL',
  'DA',
  'LT',
  'DE',
  'IT',
  'NL',
  'ES',
] as const;
export const ZodInstrumentDocumentLanguage = z.enum(InstrumentDocumentLanguageValues);
export type InstrumentDocumentLanguage = z.TypeOf<typeof ZodInstrumentDocumentLanguage>;

// also served by the public API, the dates keep the legacy schema until https://sh4res.atlassian.net/browse/LIMA-995
const ZodStructuredProductDetails = z.object({
  marketing_date_start_at: ZodDateString,
  marketing_date_end_at: ZodDateString,
  issue_price_denomination_rate: z.number(),
  issue_date_at: ZodDateString,
  strike_date_at: ZodDateString,
  maturity_date: ZodDateString,
  structured_product_type: ZodStructuredProductType,
  wm_company_ids: z.array(z.number().int()),
  issue_price_denomination_value: zodIntegerAmount2,
  minimum_trading_size: zodIntegerAmount2,
  commission_to_third_parties_percentage_rate: zodDecimalAmount4,
  one_off_entry_cost_percentage_rate: zodDecimalAmount4,
  running_cost_percentage_rate: zodDecimalAmount4,
  placement_type: ZodAssetPlacementType,
});

const ZodGetInstrumentOneResponseBase = z.object({
  ...ZodGetInstrumentsResponseItem.omit({
    country_codes_tradable: true,
    country_codes_sell_only: true,
    country_codes_suspended: true,
    performance: true,
    is_floor_price_underlying: true,
  }).shape,
  settlement_location: ZodTrimmedString.nullable(),
  sub_custodian_account_number: ZodTrimmedString.nullable(),
  has_pending_orders: z.boolean(),
  has_open_positions: z.boolean(),
  is_price_fetched: z.boolean(),
  data_fetch_completed: z.boolean(),
  risk: z.number().nullable(),
  mutual_fund_asset_class: ZodInstrumentMutualFundAssetClass.nullable(),
  management_company: ZodTrimmedString.nullable(),
  about: ZodTrimmedString.nullable(),
  management_fee_pct: z.number().nullable(),
  management_type: ZodInstrumentManagementType.nullable(),
  kid_urls: z.array(
    z.object({
      url: ZodTrimmedString,
      language: ZodInstrumentDocumentLanguage,
    }),
  ),
  prospectus_url: ZodTrimmedString.nullable(),
  about_en: ZodTrimmedString.nullable(),
  about_fr: ZodTrimmedString.nullable(),
  about_pl: ZodTrimmedString.nullable(),
  is_adr: z.boolean(),
  shares_ca_wht_rate_at_source: zodDecimalAmount.nullable(),
  is_complex: z.boolean(),
  is_ftt_rate_zero_override: z.boolean(),
  intermediate_instrument_parent_id: ZodIntegerLike.optional().nullable(),
  legal_status: ZodInstrumentMutualFundLegalStatus.nullable(),
  is_etf: z.boolean(),
  is_triggered_arbitrage_allowed: z.boolean(),
  is_single_underlying_asset: z.boolean().nullable(),
  is_withdrawal_trigger_threshold_enabled: z.boolean().nullable(),
  is_floor_nav_enabled: z.boolean().nullable(),
  is_floor_price_enabled: z.boolean().nullable(),
  // also served by the public API, keep the legacy schema until https://sh4res.atlassian.net/browse/LIMA-995
  redemption_period_start_at: ZodDateString.nullable(),
  redemption_period_end_at: ZodDateString.nullable(),
  maturity_target_instrument_id: ZodId.nullable(),
  ...nullableZodObjectProperties(ZodStructuredProductDetails).shape,
});

export const ZodGetInstrumentOneResponse = z.object({
  ...ZodGetInstrumentOneResponseBase.shape,
  entry_fees_non_acquired_percentage_rate: zodDecimalAmount4.nullable(),
  rebate_fee_rate: zodDecimalAmount5.nullable(),
  gross_ongoing_costs_ex_ante_rate: zodDecimalAmount5.nullable(),
  transaction_costs_ex_ante_rate: zodDecimalAmount5.nullable(),
  incidental_costs_ex_ante_rate: zodDecimalAmount5.nullable(),
  qty_decimal_places: z.number().int(),
  // also served by the public API, keeps the legacy schema until https://sh4res.atlassian.net/browse/LIMA-995
  ccb_last_vl_date: ZodDateString.nullable(),
  has_close_price: z.boolean().optional(),
});
export type GetInstrumentOneResponse = z.infer<typeof ZodGetInstrumentOneResponse>;

export const InstrumentsPerformanceFilterValues = [
  'position_3m',
  'position_6m',
  'position_1y',
  'position_3y',
] as const;

const ZodInstrumentsPerformanceFilter = z.enum(InstrumentsPerformanceFilterValues);

export type InstrumentsPerformanceFilter = z.infer<typeof ZodInstrumentsPerformanceFilter>;

const ZodGetInstrumentsFilters = z
  .object({
    ...ZodGetInstrumentsResponseItem.pick({
      tradable: true,
      fractionable: true,
      is_enabled: true,
      is_halted: true,
      is_pea_eligible: true,
      is_floor_price_underlying: true,
    }).shape,
    is_plan_universe_eligible: z.boolean(),
    query: ZodTrimmedString,
    id: createZodArrayable(ZodNumberQuery),
    symbol: createZodArrayable(ZodTrimmedString),
    name: createZodArrayable(ZodTrimmedString),
    exchange: createZodArrayable(ZodTrimmedString),
    class: createZodArrayable(ZodTrimmedString),
    status: createZodArrayable(ZodInstrumentBrokerStatus),
    from_created_at: createZodArrayable(ZodTrimmedString),
    to_created_at: createZodArrayable(ZodTrimmedString),
    from_redemption_period_start_at: createZodArrayable(ZodTrimmedString),
    to_redemption_period_start_at: createZodArrayable(ZodTrimmedString),
    from_redemption_period_end_at: createZodArrayable(ZodTrimmedString),
    to_redemption_period_end_at: createZodArrayable(ZodTrimmedString),
    type: createZodArrayable(ZodInstrumentType.or(ZodB2bInstrumentType)),
    asset_type: createZodArrayable(ZodInstrumentAssetType.or(ZodB2bInstrumentAssetType)),
    isin: createZodArrayable(ZodTrimmedString.nullable()),
    legal_status: createZodArrayable(ZodInstrumentMutualFundLegalStatus),
    performance: ZodInstrumentsPerformanceFilter,
    support_code: createZodArrayable(ZodTrimmedString),
  })
  .partial();

export const getInstrumentsSortColumns: (keyof GetInstrumentsResponseItem)[] = [
  'id',
  'symbol',
  'name',
  'exchange',
  'class',
  'tradable',
  'is_pea_eligible',
  'fractionable',
  'created_at',
  'logo_path',
  'short_name',
  'short_description',
  'isin',
  'is_enabled',
  'is_halted',
  'legal_status',
];

const ZodGetInstrumentsQuery = buildZodBoListQueryParsed(
  ZodGetInstrumentsFilters,
  getInstrumentsSortColumns,
  { range: [0, 100] },
);
export type GetInstrumentsQuery = BoListQueryBase<
  z.TypeOf<typeof ZodGetInstrumentsFilters>,
  (typeof getInstrumentsSortColumns)[number]
>;

export const ZodPutLogoInstrumentResponse = z.object({
  id: ZodId,
});
export type PutLogoInstrumentResponse = z.TypeOf<typeof ZodPutLogoInstrumentResponse>;

export const ZodInstrumentCountriesStatusEdit = z.object({
  country_code: ZodCountry,
  shares_status: ZodSharesStatus,
});
export type InstrumentCountriesStatusEdit = z.TypeOf<typeof ZodInstrumentCountriesStatusEdit>;

export const ZodPutInstrumentFieldsInput = z
  .object({
    ...ZodGetInstrumentOneResponse.pick({
      isin: true,
      is_enabled: true,
      is_price_fetched: true,
      short_description: true,
      country_code: true,
      is_adr: true,
      shares_ca_wht_rate_at_source: true,
      is_pea_eligible: true,
    }).shape,
    name: ZodTrimmedString.min(2).nullable(),
    short_name: ZodTrimmedString.min(2).nullable(),
    new_logo: ZodFileUploadInput.or(ZodTrimmedString).nullable(),
    is_complex: z.boolean().nullish(),
    countries_status: z.array(ZodInstrumentCountriesStatusEdit),
    five_year_beta_fallback: z.number().nullish(),
    is_single_underlying_asset: z.boolean().nullish(),
    is_withdrawal_trigger_threshold_enabled: z.boolean().nullish(),
    is_floor_nav_enabled: z.boolean().nullish(),
    is_floor_price_enabled: z.boolean().nullish(),
    qty_decimal_places: z.number().int().min(0).max(10).nullish(),
    announcement_delay_buy: z.number().int().min(0).max(10).nullish(),
    announcement_delay_sell: z.number().int().min(0).max(10).nullish(),
    ...ZodGetInstrumentOneResponse.pick({
      management_company: true,
      management_fee_pct: true,
      management_type: true,
      mutual_fund_asset_class: true,
      risk: true,
      about_en: true,
      about_pl: true,
      about_fr: true,
      settlement_location: true,
      sub_custodian_account_number: true,
      is_ftt_rate_zero_override: true,
      intermediate_instrument_parent_id: true,
      legal_status: true,
      is_triggered_arbitrage_allowed: true,
      marketing_date_start_at: true,
      marketing_date_end_at: true,
      issue_price_denomination_rate: true,
      issue_date_at: true,
      strike_date_at: true,
      maturity_date: true,
      structured_product_type: true,
      issue_price_denomination_value: true,
      minimum_trading_size: true,
      commission_to_third_parties_percentage_rate: true,
      one_off_entry_cost_percentage_rate: true,
      running_cost_percentage_rate: true,
      placement_type: true,
      entry_fees_non_acquired_percentage_rate: true,
      rebate_fee_rate: true,
      gross_ongoing_costs_ex_ante_rate: true,
      transaction_costs_ex_ante_rate: true,
      incidental_costs_ex_ante_rate: true,
      ccb_last_vl_date: true,
    }).partial().shape,
  })
  // Need to use the more explicity syntax, otherwise
  // zod wont mark about_fr and about_pl as optional
  .transform((payload) => {
    payload.about_en = payload.about_en?.trim() === '' ? null : payload.about_en;

    payload.about_fr = payload.about_fr?.trim() === '' ? null : payload.about_fr;

    payload.about_pl = payload.about_pl?.trim() === '' ? null : payload.about_pl;

    return payload;
  })
  .refine(
    (payload) => {
      if (payload.is_single_underlying_asset === true) {
        return (
          payload.is_withdrawal_trigger_threshold_enabled === false &&
          payload.is_floor_nav_enabled === false
        );
      } else if (payload.is_single_underlying_asset === false) {
        return payload.is_floor_price_enabled === false;
      }

      return true;
    },
    { error: 'Invalid configuration based on the underlying asset type' },
  )
  .meta({ override: { type: 'object' } });

export type PutInstrumentFieldsInput = z.TypeOf<typeof ZodPutInstrumentFieldsInput>;

export const ZodPutInstrumentRequest = z.object({
  id: ZodId,
  reason: ZodTrimmedString.min(5),
  instrument: ZodPutInstrumentFieldsInput,
});
export type PutInstrumentRequest = z.TypeOf<typeof ZodPutInstrumentRequest>;

const ZodPutInstrumentResponse = z.object({
  id: ZodId,
});
export type PutInstrumentResponse = z.TypeOf<typeof ZodPutInstrumentResponse>;

export const ZodGetInstrumentCountriesStatusResponseOne = z.object({
  id: ZodCountry,
  name: ZodTrimmedString,
  entity: z.enum(LegalEntityValues).nullable(),
  shares_status: ZodSharesStatus,
  instrument_id: ZodId,
});
export type GetInstrumentCountriesStatusResponseOne = z.TypeOf<
  typeof ZodGetInstrumentCountriesStatusResponseOne
>;

const ZodGetInstrumentCountriesStatusResponse = z.array(ZodGetInstrumentCountriesStatusResponseOne);
export type GetInstrumentCountriesStatusResponse = z.TypeOf<
  typeof ZodGetInstrumentCountriesStatusResponse
>;

export const getInstrumentCountriesStatusSortColumns: (keyof GetInstrumentCountriesStatusResponseOne)[] =
  ['id', 'entity', 'name'];

const ZodFilters = z
  .object({
    ...ZodGetInstrumentCountriesStatusResponseOne.pick({
      instrument_id: true,
      name: true,
      id: true,
    }).shape,
    query: ZodTrimmedString,
  })
  .partial();

const ZodGetInstrumentCountriesStatusQuery = buildZodBoListQueryParsed(
  ZodFilters,
  getInstrumentCountriesStatusSortColumns,
  {
    range: [0, 100],
  },
);

export type GetInstrumentCountriesStatusQuery = BoListQueryBase<
  z.TypeOf<typeof ZodFilters>,
  (typeof getInstrumentCountriesStatusSortColumns)[number]
>;

export const getInstrumentsIdEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodIDPathParam,
  responseBody: ZodGetInstrumentOneResponse,
});

export type GetInstrumentsIdEndpoint = GetEndpointType<typeof getInstrumentsIdEndpoint>;

export const putInstrumentsIdEndpoint = endpoint({
  method: 'PUT',
  pathParams: ZodIDPathParam,
  body: ZodPutInstrumentRequest,
  responseBody: ZodPutInstrumentResponse,
});

export type PutInstrumentsIdEndpoint = GetEndpointType<typeof putInstrumentsIdEndpoint>;

export const ZodPostInstrumentStructuredProduct = z
  .object({
    isin: ZodTrimmedString,
    logo: ZodFileUploadInput.nullable(),
    name: ZodTrimmedString,
    short_name: ZodTrimmedString,
    management_company: ZodTrimmedString,
    qty_decimal_places: z.number().int().min(0).max(6),
    ...ZodStructuredProductDetails.omit({ wm_company_ids: true }).shape,
  })
  .refine(
    (val) => new Date(val.marketing_date_start_at) < new Date(val.marketing_date_end_at),
    'Marketing end date must be after marketing start date',
  );
export type PostInstrumentStructuredProduct = z.TypeOf<typeof ZodPostInstrumentStructuredProduct>;

export const ZodPostInstrumentRequest = z.discriminatedUnion('type', [
  z.object({ type: z.literal('structured-product'), data: ZodPostInstrumentStructuredProduct }),
]);

export type PostInstrumentRequest = z.TypeOf<typeof ZodPostInstrumentRequest>;

export const ZodPostInstrumentResponse = z.object({
  id: ZodId,
});

export type PostInstrumentResponse = z.TypeOf<typeof ZodPostInstrumentResponse>;

export const postInstrumentEndpoint = endpoint({
  method: 'POST',
  body: ZodPostInstrumentRequest,
  responseBody: ZodPostInstrumentResponse,
});

export type PostInstrumentEndpoint = GetEndpointType<typeof postInstrumentEndpoint>;

export const getInstrumentsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetInstrumentsQuery,
  responseBody: ZodGetInstrumentsResponse,
});

export type GetInstrumentsEndpoint = GetEndpointType<typeof getInstrumentsEndpoint>;

export const ZodInstrumentsUniverseAcknowledgeResponseItem = z.object({
  instrument_id: ZodId.nullable(),
  isin: ZodTrimmedString,
  feedback: ZodTrimmedString,
});

export type InstrumentsUniverseAcknowledgeResponseItem = z.TypeOf<
  typeof ZodInstrumentsUniverseAcknowledgeResponseItem
>;

export const postInstrumentsUniverseAcknowledgeEndpoint = endpoint({
  method: 'POST',
  body: z.object({
    type: z.enum(['amend_universe', 'enable_assets']),
    presigned_path: ZodTrimmedString,
  }),
  responseBody: z.object({
    items: z.array(ZodInstrumentsUniverseAcknowledgeResponseItem),
  }),
});

export type PostInstrumentsUniverseAcknowledgeEndpoint = GetEndpointType<
  typeof postInstrumentsUniverseAcknowledgeEndpoint
>;

export const getInstrumentsCountriesStatusEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetInstrumentCountriesStatusQuery,
  responseBody: ZodGetInstrumentCountriesStatusResponse,
});

export type GetInstrumentsCountriesStatusEndpoint = GetEndpointType<
  typeof getInstrumentsCountriesStatusEndpoint
>;

export const InstrumentDocumentTypeValues = [
  'kid',
  'prospectus',
  'complexity_and_target_market_assessment',
  'term_sheet',
  'brochure',
] as const;
const ZodInstrumentDocumentType = z.enum(InstrumentDocumentTypeValues);
export type InstrumentDocumentType = z.TypeOf<typeof ZodInstrumentDocumentType>;
export const isInstrumentDocumentPublic = (type: InstrumentDocumentType) =>
  match(type)
    .with('complexity_and_target_market_assessment', () => false)
    .with('kid', 'prospectus', 'term_sheet', 'brochure', () => true)
    .exhaustive();

const ZodGetInstrumentDocumentsFilters = z.object({
  instrument_id: ZodId,
  type: ZodInstrumentDocumentType.optional(),
  language: ZodInstrumentDocumentLanguage.optional(),
});

export type GetInstrumentDocumentsFilters = z.TypeOf<typeof ZodGetInstrumentDocumentsFilters>;

const ZodGetInstrumentDocumentsResponseOne = z.object({
  id: ZodId,
  instrument_id: ZodId,
  type: ZodTrimmedString,
  url: ZodTrimmedString,
  language: ZodTrimmedString,
  is_public: z.boolean(),
  created_at: z.date(),
  updated_at: z.date(),
  archived: z.boolean(),
});

export type GetInstrumentDocumentsResponseOne = z.TypeOf<
  typeof ZodGetInstrumentDocumentsResponseOne
>;

export const ZodGetInstrumentDocumentsResponse = z.array(ZodGetInstrumentDocumentsResponseOne);

export const getInstrumentDocumentsSortColumns: (keyof GetInstrumentDocumentsResponseOne)[] = [
  'id',
  'type',
  'url',
  'language',
  'created_at',
  'archived',
];

export const ZodGetInstrumentDocumentsQuery = buildZodBoListQueryParsed(
  ZodGetInstrumentDocumentsFilters,
  getInstrumentDocumentsSortColumns,
  { range: [0, 10] },
);
export type GetInstrumentDocumentsQuery = BoListQueryBase<
  z.TypeOf<typeof ZodGetInstrumentDocumentsFilters>,
  (typeof getInstrumentDocumentsSortColumns)[number]
>;

export const getInstrumentDocumentsEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetInstrumentDocumentsQuery,
  responseBody: ZodGetInstrumentDocumentsResponse,
});

export type GetInstrumentDocumentsEndpoint = GetEndpointType<typeof getInstrumentDocumentsEndpoint>;

const ZodPostInstrumentDocumentRequest = z.object({
  instrument_id: ZodId,
  type: ZodInstrumentDocumentType,
  language: ZodInstrumentDocumentLanguage,
  presigned_path: ZodTrimmedString,
});

const ZodPostInstrumentDocumentResponse = z.object({
  id: ZodId,
  instrument_id: ZodId,
  type: ZodInstrumentDocumentType,
  language: ZodInstrumentDocumentLanguage,
  url: ZodTrimmedString,
});

export type PostInstrumentDocumentRequest = z.infer<typeof ZodPostInstrumentDocumentRequest>;

export const postInstrumentDocumentsEndpoint = endpoint({
  method: 'POST',
  body: ZodPostInstrumentDocumentRequest,
  responseBody: ZodPostInstrumentDocumentResponse,
});

export type PostInstrumentDocumentsEndpoint = GetEndpointType<
  typeof postInstrumentDocumentsEndpoint
>;

export const deleteInstrumentDocumentsIdEndpoint = endpoint({
  method: 'DELETE',
  pathParams: ZodIDPathParam,
  responseBody: ZodSuccessResponse,
});

export type DeleteInstrumentDocumentsIdEndpoint = GetEndpointType<
  typeof deleteInstrumentDocumentsIdEndpoint
>;

/**
 * Cutoff calendars only apply to mutual funds whose ISIN starts with QS or 99
 * (funds traded via the qs99/manual ODDO execution flows).
 */
export const isQs99CutoffIsin = (isin: string | null): boolean =>
  G.isNotNullable(isin) && (isin.startsWith('QS') || isin.startsWith('99'));

const ZodGetInstrumentCutoffDatesFilters = z.object({
  instrument_id: ZodId,
  from_cutoff_date: ZodDateString.optional(),
  to_cutoff_date: ZodDateString.optional(),
});

export type GetInstrumentCutoffDatesFilters = z.TypeOf<typeof ZodGetInstrumentCutoffDatesFilters>;

const ZodGetInstrumentCutoffDatesResponseOne = z.object({
  id: ZodId,
  instrument_id: ZodId,
  cutoff_date: ZodBrandedDateString,
});

export type GetInstrumentCutoffDatesResponseOne = z.TypeOf<
  typeof ZodGetInstrumentCutoffDatesResponseOne
>;

export const ZodGetInstrumentCutoffDatesResponse = z.array(ZodGetInstrumentCutoffDatesResponseOne);

export const getInstrumentCutoffDatesSortColumns: (keyof GetInstrumentCutoffDatesResponseOne)[] = [
  'cutoff_date',
];

export const ZodGetInstrumentCutoffDatesQuery = buildZodBoListQueryParsed(
  ZodGetInstrumentCutoffDatesFilters,
  getInstrumentCutoffDatesSortColumns,
  { range: [0, 24], sort: ['cutoff_date', 'ASC'] },
);
export type GetInstrumentCutoffDatesQuery = BoListQueryBase<
  z.TypeOf<typeof ZodGetInstrumentCutoffDatesFilters>,
  (typeof getInstrumentCutoffDatesSortColumns)[number]
>;

export const getInstrumentCutoffDatesEndpoint = endpoint({
  method: 'GET',
  queryParams: ZodGetInstrumentCutoffDatesQuery,
  responseBody: ZodGetInstrumentCutoffDatesResponse,
});

export type GetInstrumentCutoffDatesEndpoint = GetEndpointType<
  typeof getInstrumentCutoffDatesEndpoint
>;

const ZodPostInstrumentCutoffDatesBulkUpdateRequest = z
  .object({
    instrument_id: ZodId,
    add: z.array(ZodBrandedDateString).default([]),
    remove: z.array(ZodBrandedDateString).default([]),
  })
  .refine(({ add, remove }) => add.length > 0 || remove.length > 0, {
    message: 'At least one date must be added or removed',
  })
  .refine(({ add, remove }) => !add.some((date) => remove.includes(date)), {
    message: 'The same date cannot be both added and removed',
  });

export type PostInstrumentCutoffDatesBulkUpdateRequest = z.infer<
  typeof ZodPostInstrumentCutoffDatesBulkUpdateRequest
>;

export const postInstrumentCutoffDatesBulkUpdateEndpoint = endpoint({
  method: 'POST',
  body: ZodPostInstrumentCutoffDatesBulkUpdateRequest,
  responseBody: z.object({ id: ZodId }),
});

export type PostInstrumentCutoffDatesBulkUpdateEndpoint = GetEndpointType<
  typeof postInstrumentCutoffDatesBulkUpdateEndpoint
>;
